<template>
  <modal
    width="560px"
    bodyPadding="0"
    :title="t('Change Password')"
    @close="close"
    :readArticleUrl="`https://docs.bemob.com/${i18n.global.locale.value}/security`"
  >
    <div class="wrapper">
      <div class="wrapper-container">
        <ui-input
          v-model="form.oldPassword"
          name="oldPassword"
          :label="t('Old Password')"
          :placeholder="t('Old Password')"
          autofocus
          :type="showPassword ? 'text' : 'password'"
          :append-inner-icon="showPassword ? 'fas fa-lock-open-alt' : 'fas fa-lock-alt'"
          :error-messages="fieldErrors.oldPassword"
          @change="clearFieldError('oldPassword')"
          @click:appendInner="() => (showPassword = !showPassword)"
        />
        <ui-input
          v-model="form.password"
          name="password"
          :label="t('New Password')"
          :placeholder="t('New Password')"
          :type="showPassword ? 'text' : 'password'"
          :append-inner-icon="showPassword ? 'fas fa-lock-open-alt' : 'fas fa-lock-alt'"
          :error-messages="fieldErrors.password"
          @change="clearFieldError('password')"
          @click:appendInner="() => (showPassword = !showPassword)"
        />
        <ui-input
          v-model="form.confirmPassword"
          name="confirmPassword"
          :label="t('Repeat Password')"
          :placeholder="t('Repeat Password')"
          :type="showPassword ? 'text' : 'password'"
          :append-inner-icon="showPassword ? 'fas fa-lock-open-alt' : 'fas fa-lock-alt'"
          :error-messages="fieldErrors.confirmPassword"
          @change="clearFieldError('confirmPassword')"
          @click:appendInner="() => (showPassword = !showPassword)"
          @keyup.enter="submit"
        />
      </div>
    </div>

    <template #footer>
      <div class="d-flex justify-space-between align-center">
        <error-aggregator v-model="errors" v-model:active="errorsControlActive" />
        <div class="d-flex">
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            :disabled="apiLoading"
            @click="close"
          />
          <ui-button
            class="ml-8"
            :text="t('Change Password')"
            icon="fas fa-check"
            button-style="accent"
            :loading="apiLoading"
            @click="submit"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useApiFieldErrors, useI18n } from '@/composables';
import { putUserApiRequest } from '@/services/api/user-api';
import type { FieldErrors, User } from '@/services/models';
import { $emitter } from '@/services/emitter';
import i18n from '@/i18n';

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const {
  fieldErrors,
  errors,
  errorsControlActive,
  clearErrors,
  applyApiErrors,
} = useApiFieldErrors();

const apiLoading = ref(false);
const showPassword = ref(false);

interface ChangePasswordForm {
  oldPassword: string;
  password: string;
  confirmPassword: string;
}

const form = reactive<ChangePasswordForm>({
  oldPassword: '',
  password: '',
  confirmPassword: '',
});

const clearFieldError = (field: keyof ChangePasswordForm) => {
  const newValue = { ...fieldErrors.value };
  delete newValue[field];
  fieldErrors.value = newValue;
};

const close = () => {
  clearErrors();
  emit('close');
};

const submit = async () => {
  clearErrors();

  const nextFieldErrors: FieldErrors = {};
  let validationMessages: string[] = [];

  if (!form.oldPassword) {
    const errorText = t('{0} is required', [t('Old password')]);
    nextFieldErrors.oldPassword = [errorText];
    validationMessages.push(errorText);
  }

  if (!form.password) {
    const errorText = t('{0} is required', [t('New password')]);
    nextFieldErrors.password = [errorText];
    validationMessages.push(errorText);
  }

  if (!form.confirmPassword) {
    const errorText = t('{0} is required', [t('Repeat Password')]);
    nextFieldErrors.confirmPassword = [errorText];
    validationMessages.push(errorText);
  }

  if (form.password !== form.confirmPassword) {
    validationMessages = [
      t('The values in New Password and Repeat Password fields should match'),
    ];
  }

  if (validationMessages.length) {
    errors.value = [
      { message: t('Validation failed') },
      ...validationMessages.map((message) => ({ message })),
    ];
    errorsControlActive.value = true;
    fieldErrors.value = nextFieldErrors;
    return;
  }

  apiLoading.value = true;
  try {
    await putUserApiRequest(form as unknown as Partial<User>);
    Object.assign(form, { oldPassword: '', password: '', confirmPassword: '' });
    close();
    $emitter.emit('toast-success', t('Password has been successfully changed'));
  } catch (error: unknown) {
    applyApiErrors(error, { nested: true });
  } finally {
    apiLoading.value = false;
  }
};
</script>

<style lang="scss" scoped>

.wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}
.wrapper-container {
  width: 300px;
  padding: 24px;
}
</style>
